import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { Button } from "../button";
import {
  DOWNLOADABLE,
  DownloadProgress,
  OFFLINE_ENABLED,
  OfflineGroup,
  OfflineManifest,
  clearDownload,
  download,
  fetchManifest,
  formatBytes,
  status,
} from "../../utils/offline-manager";

/**
 * The "available offline" section of the sidebar.
 *
 * Two principles shape this panel. First, nothing large is ever downloaded
 * without being asked for: the groups are listed with real sizes so the user
 * decides whether 234 MB of dictionaries is worth it on their connection.
 * Second, everything it does is undoable from the same panel - a download that
 * cannot be deleted from the same place it was started is a trap.
 */

const Section = styled.div`
  margin-top: 12px;
  border-top: 1px solid ${props => props.theme.colors.gray};
  padding-top: 8px;
`;

const SectionTitle = styled.div`
  font-weight: bold;
  text-align: center;
  font-size: 0.8em;
  cursor: pointer;
  margin-bottom: 6px;
`;

const GroupRow = styled.label`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.85em;
  padding: 2px 0;
  cursor: pointer;
`;

const GroupName = styled.span`
  flex: 1;
`;

const GroupSize = styled.span`
  opacity: 0.7;
  font-variant-numeric: tabular-nums;
`;

const Note = styled.div<{ $warning?: boolean }>`
  font-size: 0.75em;
  opacity: ${props => (props.$warning ? 1 : 0.7)};
  color: ${props => (props.$warning ? props.theme.colors.red : "inherit")};
  margin: 6px 0;
  line-height: 1.35;
`;

const Bar = styled.div`
  height: 4px;
  background: ${props => props.theme.colors.gray};
  border-radius: 2px;
  overflow: hidden;
  margin: 6px 0;
`;

const BarFill = styled.div<{ $percent: number }>`
  height: 100%;
  width: ${props => props.$percent}%;
  background: ${props => props.theme.colors.red};
  transition: width 0.2s linear;
`;

const Actions = styled.div`
  display: flex;
  gap: 4px;
  margin-top: 6px;

  > * {
    flex: 1;
    padding: 5px;
    height: 28px;
  }
`;

export const OfflinePanel: React.FC = () => {
  const { t } = useTranslation();

  const [open, setOpen] = useState(
    typeof window === "undefined" ? false : JSON.parse(window.localStorage.getItem("offlinePanelOpen") || "false")
  );
  const [manifest, setManifest] = useState<OfflineManifest | null>(null);
  const [selected, setSelected] = useState<Set<OfflineGroup>>(
    new Set(DOWNLOADABLE.filter(group => group.defaultOn).map(group => group.id))
  );
  const [progress, setProgress] = useState<DownloadProgress | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [state, setState] = useState<Awaited<ReturnType<typeof status>> | null>(null);

  const refreshStatus = () => status().then(setState).catch(() => {});

  // The manifest is only fetched once the section is expanded. It is a few
  // hundred kilobytes of URL list, and most visitors will never open this.
  useEffect(() => {
    if (!open || manifest) return;
    fetchManifest().then(setManifest).catch(e => setError(e.message));
    refreshStatus();
  }, [open, manifest]);

  const toggleOpen = () => {
    const next = !open;
    window.localStorage.setItem("offlinePanelOpen", JSON.stringify(next));
    setOpen(next);
  };

  const toggleGroup = (id: OfflineGroup) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelected(next);
  };

  // core is always included, so it counts towards the total the user is shown.
  const selectedBytes = manifest
    ? manifest.sizes.core + [...selected].reduce((total, id) => total + (manifest.sizes[id] || 0), 0)
    : 0;

  const startDownload = async () => {
    setBusy(true);
    setError(null);
    setProgress(null);
    try {
      await download([...selected], setProgress);
      await refreshStatus();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const removeDownload = async () => {
    setBusy(true);
    try {
      await clearDownload();
      setProgress(null);
      await refreshStatus();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  // Nothing to show when the build did not ship a worker. Without this the
  // panel appears on a site that has offline mode switched off, and expanding it
  // reports "Manifest unavailable (404)" next to a dead DOWNLOAD (0 B) button -
  // advertising a feature that cannot work. The section has to disappear
  // entirely, not merely degrade.
  if (!OFFLINE_ENABLED) return null;

  // Rendered only where a worker can actually run. Without this the panel would
  // offer a download that silently does nothing on unsupported browsers.
  if (typeof window !== "undefined" && !("serviceWorker" in navigator)) return null;

  return (
    <Section>
      <SectionTitle onClick={toggleOpen}>
        {t("offline-title")} {open ? "▼" : "▲"}
      </SectionTitle>

      {open && (
        <>
          {state && !state.registered && <Note $warning>{t("offline-unavailable")}</Note>}

          {error && <Note $warning>{error}</Note>}

          {manifest &&
            DOWNLOADABLE.map(group => (
              <GroupRow key={group.id}>
                <input
                  type="checkbox"
                  checked={selected.has(group.id)}
                  disabled={busy}
                  onChange={() => toggleGroup(group.id)}
                />
                <GroupName>{t(`offline-group-${group.id}`)}</GroupName>
                <GroupSize>{formatBytes(manifest.sizes[group.id] || 0)}</GroupSize>
              </GroupRow>
            ))}

          {progress && (
            <>
              <Bar>
                <BarFill $percent={progress.total ? (progress.done / progress.total) * 100 : 0} />
              </Bar>
              <Note>
                {progress.done} / {progress.total}
                {progress.failed > 0 && ` · ${progress.failed} ${t("offline-failed")}`}
              </Note>
            </>
          )}

          {/* Chrome only grants persistence on sites it considers "engaged".
              Saying so is better than letting a 400 MB download be evicted
              without explanation. */}
          {state && state.hasDownload && !state.persisted && (
            <Note $warning>{t("offline-not-persisted")}</Note>
          )}

          {state && state.usageBytes > 0 && (
            <Note>
              {t("offline-stored")}: {formatBytes(state.usageBytes)}
              {state.quotaBytes > 0 && ` / ${formatBytes(state.quotaBytes)}`}
            </Note>
          )}

          <Actions>
            <Button onClick={startDownload} value="download" disabled={busy || !manifest}>
              {busy ? t("offline-working") : `${t("offline-download")} (${formatBytes(selectedBytes)})`}
            </Button>
            {state?.hasDownload && (
              <Button onClick={removeDownload} value="clear" disabled={busy}>
                {t("offline-clear")}
              </Button>
            )}
          </Actions>
        </>
      )}
    </Section>
  );
};
